import { useLDClient } from 'launchdarkly-react-client-sdk'
import type { FeatureFlag } from '../feature-flag-types'
import { getFlagValue } from '../feature-flag-helpers'

/**
 * Widen boolean literal types (true/false) to boolean
 * This allows flags with defaultValue: false to be typed as boolean instead of literal false
 */
type WidenBoolean<T> = T extends boolean ? boolean : T

/**
 * Infer the type of a feature flag from its defaultValue
 * Boolean flags (defaultValue: true/false) are widened to boolean
 * Other types are preserved as-is
 */
type InferFlagType<T> = T extends { readonly defaultValue: infer D } ? WidenBoolean<D> : unknown

/**
 * Hook to access feature flags using the multiple flags pattern
 * Always receives an object mapping keys to flag definitions
 * Always returns an object with the evaluated flag values
 *
 * @param flags - Object mapping keys to flag definitions
 * @returns Object with flag values - types are automatically inferred from defaultValue
 *
 * @example
 * // Single flag - still uses object pattern
 * import { useFeatureFlags, FeatureFlagName } from '@/infra/feature-flag'
 *
 * function MyComponent() {
 *   const { i18nEnabled } = useFeatureFlags({
 *     i18nEnabled: FeatureFlagName.I18N
 *   })
 *   // i18nEnabled is automatically typed as boolean
 * }
 *
 * @example
 * // Multiple flags - types are automatically inferred
 * function MyComponent() {
 *   const { i18n, userSpace } = useFeatureFlags({
 *     i18n: FeatureFlagName.I18N,
 *     userSpace: FeatureFlagName.USER_SPACE_ENABLED
 *   })
 *   // i18n and userSpace are automatically typed as boolean
 * }
 *
 * @example
 * // Multiple flags with variation values
 * function MyComponent() {
 *   const { helpCenter } = useFeatureFlags({
 *     helpCenter: FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER
 *   })
 *   // helpCenter type is inferred from the flag's defaultValue
 *   // For special cases, you can override: useFeatureFlags<{ helpCenter: CustomType[] }>(...)
 * }
 */
export function useFeatureFlags<T extends Record<string, FeatureFlag>>(
  flags: T
): { [K in keyof T]: InferFlagType<T[K]> } {
  const ldClient = useLDClient()
  const results: Record<string, unknown> = {}

  for (const key in flags) {
    results[key] = getFlagValue(flags[key], ldClient)
  }

  return results as { [K in keyof T]: InferFlagType<T[K]> }
}
